import { useState, useMemo } from 'react'
import { usePopper } from 'react-popper'
import { PROVIDER_ID } from 'wallet/index'
import useWalletManager from 'hooks/useWalletManager'
import useWallet from 'hooks/useWallet'
import type { Account } from 'wallet/index'

export type TruncatedAddress = {
  start: {
    xs: string
    sm: string
  }
  end: string
}

export default function useConnectWallet() {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)

  const { styles: popperStyles, attributes: popperAttributes } = usePopper(
    referenceElement,
    popperElement,
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 11]
          }
        }
      ]
    }
  )

  const walletManager = useWalletManager()
  const wallet = useWallet()

  const handleConnectWallet = async (walletId: PROVIDER_ID | null) => {
    if (!walletId) {
      throw new Error('No wallet id')
    }

    try {
      await walletManager.connect(walletId)
    } catch (e) {
      /** @todo handle error gracefully */
      console.error(e)
    }
  }

  const handleDisconnectWallet = async (walletId: PROVIDER_ID) => {
    if (!walletId) {
      throw new Error('No wallet id')
    }

    try {
      await walletManager.disconnect(walletId)
    } catch (e) {
      /** @todo handle error gracefully */
      console.error(e)
    }
  }

  const handleSetActiveAccount = (account: Account) => {
    walletManager.setActiveAccount(account)
  }

  const hasConnectedAccountDeps = JSON.stringify(walletManager.accounts)
  const hasConnectedAccount = useMemo(
    () => walletManager.accounts?.some((account) => account.hasOwnProperty('providerId')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasConnectedAccountDeps]
  )

  return {
    walletManager,
    wallet,
    popperStyles,
    popperAttributes,
    setReferenceElement,
    setPopperElement,
    handleConnectWallet,
    handleDisconnectWallet,
    handleSetActiveAccount,
    hasConnectedAccount
  }
}
